import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { CreditCard, Building2, Globe, ArrowLeft, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentMethodSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceType, serviceDuration, amount, serviceTitle, planDetails, ppuDetails } = location.state || {};
  
  const [selectedMethod, setSelectedMethod] = useState('bank_transfer');
  const [processing, setProcessing] = useState(false);

  const handleContinue = async () => {
    if (selectedMethod === 'bank_transfer') {
      navigate('/payment/bank-transfer', {
        state: { serviceType, serviceDuration, amount, serviceTitle, planDetails, ppuDetails }
      });
    } else if (selectedMethod === 'international') {
      // Handle Stripe international payment
      setProcessing(true);
      const token = localStorage.getItem('token');
      
      try {
        let response;
        
        // Check if this is a membership payment
        if (serviceType?.startsWith('membership_')) {
          const planId = serviceType.replace('membership_', '');
          response = await fetch(`${API_URL}/api/payments/membership/checkout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              plan_id: planId,
              origin_url: window.location.origin
            })
          });
        } 
        // Check if this is a boost payment
        else if (serviceType?.startsWith('boost_')) {
          const [, listingId, packageKey] = serviceType.split('_');
          response = await fetch(`${API_URL}/api/payments/boost/checkout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              listing_id: listingId,
              package_key: packageKey,
              origin_url: window.location.origin
            })
          });
        }
        // For PPU or other services, we can add more handlers here
        else {
          alert('International payment is not yet available for this service type. Please use Bank Transfer.');
          setProcessing(false);
          return;
        }

        if (response && response.ok) {
          const data = await response.json();
          if (data.checkout_url) {
            window.location.href = data.checkout_url;
          }
        } else {
          const error = await response?.json();
          alert(error?.detail || 'Failed to create checkout session');
        }
      } catch (err) {
        console.error('Error creating Stripe checkout:', err);
        alert('Failed to process international payment. Please try Bank Transfer.');
      } finally {
        setProcessing(false);
      }
    } else {
      alert('This payment method is coming soon!');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Select Payment Method</h1>
          <p className="text-gray-600 mt-2">Choose how you'd like to pay for your service</p>
        </div>

        {/* Service Summary */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Service</p>
                <p className="text-lg font-semibold">{serviceTitle || 'Premium Service'}</p>
                {serviceDuration && <p className="text-sm text-gray-500">{serviceDuration} days</p>}
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Amount</p>
                <p className="text-2xl font-bold text-blue-600">Rs. {amount?.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Methods */}
        <Card>
          <CardHeader>
            <CardTitle>Payment Methods</CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup value={selectedMethod} onValueChange={setSelectedMethod} className="space-y-4">
              {/* Bank Transfer */}
              <div
                className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedMethod === 'bank_transfer'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedMethod('bank_transfer')}
              >
                <RadioGroupItem value="bank_transfer" id="bank_transfer" />
                <Building2 className="h-6 w-6 text-blue-600" />
                <div className="flex-1">
                  <Label htmlFor="bank_transfer" className="text-base font-semibold cursor-pointer">
                    Bank Transfer (Manual)
                  </Label>
                  <p className="text-sm text-gray-500">
                    Transfer to our bank account and upload proof
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      ✓ Available Now
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      Verification: 2-24 hours
                    </span>
                  </div>
                </div>
              </div>

              {/* PayHere (Coming Soon) */}
              <div
                className="flex items-center space-x-3 p-4 rounded-lg border-2 border-gray-200 opacity-60 cursor-not-allowed"
              >
                <RadioGroupItem value="payhere" id="payhere" disabled />
                <CreditCard className="h-6 w-6 text-gray-400" />
                <div className="flex-1">
                  <Label htmlFor="payhere" className="text-base font-semibold text-gray-500">
                    Online Payment (PayHere)
                  </Label>
                  <p className="text-sm text-gray-400">
                    Pay with cards, mobile banking, or e-wallets
                  </p>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded mt-2 inline-block">
                    Coming Soon
                  </span>
                </div>
              </div>

              {/* International Payment (Stripe) - Now Available */}
              <div
                className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedMethod === 'international'
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedMethod('international')}
              >
                <RadioGroupItem value="international" id="international" />
                <Globe className="h-6 w-6 text-purple-600" />
                <div className="flex-1">
                  <Label htmlFor="international" className="text-base font-semibold cursor-pointer">
                    International Payment
                  </Label>
                  <p className="text-sm text-gray-500">
                    Pay with Stripe (Credit/Debit Cards)
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      ✓ Available Now
                    </span>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      Instant Activation
                    </span>
                  </div>
                </div>
              </div>
            </RadioGroup>

            <Button
              onClick={handleContinue}
              className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg"
              disabled={!selectedMethod || processing}
            >
              {processing ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Continue to Payment'
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="mt-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-900">
              <strong>💡 Payment Options:</strong>
            </p>
            <ul className="text-sm text-blue-800 mt-2 space-y-1">
              <li>• <strong>Bank Transfer:</strong> Best for local payments. Verification takes 2-24 hours.</li>
              <li>• <strong>International Payment (Stripe):</strong> Best for credit/debit cards. Instant activation after payment.</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentMethodSelection;
